import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import MemberPhotoPlaceholder from "@/components/base/MemberPhotoPlaceholder";
import { normalizePhotoPosition, PHOTO_POSITION_CLASS } from "@/lib/avatar";

interface TestimonialCard {
  id: number;
  text: string;
  member_name: string;
  member_photo: string | null;
  member_photo_position: string | null;
  member_profession: string;
}

export default function TestimonialSection() {
  const [testimonials, setTestimonials] = useState<TestimonialCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data, error } = await supabase
        .from("testimonials")
        .select("id, text, member_id")
        .eq("status", "approved")
        .order("created_at", { ascending: false });

      if (error || !data || data.length === 0) {
        setTestimonials([]);
        setLoading(false);
        return;
      }

      // Fetch member data for each testimonial
      const memberIds = [...new Set(data.map((t) => t.member_id))];
      const { data: membersData } = await supabase
        .from("members_public")
        .select("id, full_name, photo, photo_position, profession")
        .in("id", memberIds);

      const memberMap = new Map();
      if (membersData) {
        membersData.forEach((m: any) => {
          memberMap.set(m.id, {
            name: m.full_name,
            photo: m.photo,
            photo_position: m.photo_position || "top",
            profession: m.profession || "",
          });
        });
      }

      const merged: TestimonialCard[] = data.map((t) => ({
        id: t.id,
        text: t.text,
        member_name: memberMap.get(t.member_id)?.name || "Integrante",
        member_photo: memberMap.get(t.member_id)?.photo || null,
        member_photo_position: memberMap.get(t.member_id)?.photo_position || null,
        member_profession: memberMap.get(t.member_id)?.profession || "",
      }));

      setTestimonials(merged);
      setLoading(false);
    }
    load();
  }, []);

  // Don't render anything if no approved testimonials
  if (loading) return null;
  if (testimonials.length === 0) return null;

  const expandedTestimonial = expandedId !== null
    ? testimonials.find((t) => t.id === expandedId)
    : null;

  return (
    <>
      <section id="depoimentos" className="w-full bg-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

        <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
              Depoimentos
            </p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-brown">
              O que elas dizem sobre a comunidade
            </h2>
            <p className="font-body text-brown/70 mt-4 max-w-xl mx-auto text-base leading-relaxed">
              Histórias reais de mulheres que encontraram apoio, conexão e crescimento no Entre Elas.
            </p>
          </div>

          {/* Grid layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {testimonials.map((t) => {
              const isLong = t.text.length > 150;
              const displayText = isLong ? t.text.slice(0, 150).trim() + "..." : t.text;

              return (
                <div
                  key={t.id}
                  className="group bg-beige/60 rounded-2xl p-6 md:p-7 flex flex-col transition-all duration-300 hover:bg-beige"
                >
                  {/* Member info */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden shrink-0">
                      {t.member_photo ? (
                        <img
                          src={t.member_photo}
                          alt={t.member_name}
                          className={`w-full h-full object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(t.member_photo_position)]}`}
                        />
                      ) : (
                        <MemberPhotoPlaceholder size="sm" rounded="full" name={t.member_name} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-display text-sm text-brown truncate">
                        {t.member_name}
                      </p>
                      {t.member_profession && (
                        <p className="font-body text-xs text-primary/70 truncate">
                          {t.member_profession}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Quote text */}
                  <div className="relative flex-1">
                    <i className="ri-double-quotes-l text-2xl text-primary/15 absolute -top-1 -left-1" />
                    <p className="font-body text-sm text-brown/70 leading-relaxed pl-5 line-clamp-3">
                      {displayText}
                    </p>
                  </div>

                  {/* Read more button */}
                  {isLong && (
                    <button
                      onClick={() => setExpandedId(t.id)}
                      className="mt-4 self-start inline-flex items-center gap-1.5 font-body text-xs text-primary hover:text-primary-dark transition-colors cursor-pointer"
                    >
                      <span>Ler mais</span>
                      <i className="ri-arrow-right-line" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Leave a testimonial link */}
          <div className="text-center mt-10 md:mt-12">
            <Link
              to="/depoimento"
              className="inline-flex items-center gap-1.5 font-body text-sm text-primary/60 hover:text-primary transition-colors whitespace-nowrap cursor-pointer"
            >
              Deixe o seu comentário
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Read more modal */}
      {expandedTestimonial && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm"
          onClick={() => setExpandedId(null)}
        >
          <div
            className="relative bg-white rounded-2xl max-w-lg w-full p-8 md:p-10 shadow-lg animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setExpandedId(null)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-brown/40 hover:text-brown hover:bg-beige transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>

            {/* Member info */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full overflow-hidden shrink-0">
                {expandedTestimonial.member_photo ? (
                  <img
                    src={expandedTestimonial.member_photo}
                    alt={expandedTestimonial.member_name}
                    className={`w-full h-full object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(expandedTestimonial.member_photo_position)]}`}
                  />
                ) : (
                  <MemberPhotoPlaceholder size="md" rounded="full" name={expandedTestimonial.member_name} />
                )}
              </div>
              <div>
                <h3 className="font-display text-lg text-brown">
                  {expandedTestimonial.member_name}
                </h3>
                {expandedTestimonial.member_profession && (
                  <p className="font-body text-sm text-primary">
                    {expandedTestimonial.member_profession}
                  </p>
                )}
              </div>
            </div>

            {/* Full text */}
            <div className="relative">
              <i className="ri-double-quotes-l text-4xl text-primary/10 absolute -top-3 -left-1" />
              <p className="font-body text-brown/80 leading-relaxed text-sm md:text-base pl-6">
                {expandedTestimonial.text}
              </p>
            </div>

            {/* Close CTA */}
            <button
              onClick={() => setExpandedId(null)}
              className="mt-8 w-full py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </>
  );
}